import React, { ReactElement, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';

import { IconName } from 'assets/icons';
import { Paragraph } from 'common/components';
import { ColorType } from 'common/types/theme';

interface IChipProps {
  text: string;
  color?: ColorType;
  activeColor?: ColorType;
  bgColor?: ColorType;
  activeBgColor?: ColorType;
  size?: string;
  padding?: string;
  gap?: string;
  chipAction?: () => void;
  activeStateChange?: boolean;
  borderColor?: ColorType;
  iconName?: IconName;
  iconColor?: ColorType;
  icon?: ReactElement;
  leftIcon?: ReactElement;
}

const ChipWrapper = styled.div<{
  active?: boolean;
  bgColor: ColorType;
  size?: string;
  padding?: string;
  gap?: string;
  borderColor?: ColorType;
}>`
  display: inline-flex;
  text-wrap: nowrap;
  align-items: center;
  justify-content: center;
  gap: ${({ gap }) => gap};
  padding: ${({ padding }) => padding};
  border-radius: 32px;
  background-color: ${({ bgColor, theme }) => theme.colors[bgColor]};
  border: ${({ borderColor, theme }) =>
    borderColor ? `1px solid ${theme.colors[borderColor]}` : 'none'};
  cursor: pointer;
  transition: background-color 0.3s ease;

  ${({ active }) =>
    active &&
    css`
      font-weight: bold;
      color: ${({ theme }) => theme.colors.primary_90};
    `};
`;

export const Chip = ({
  text,
  color = 'primary_90',
  bgColor = 'primary_20',
  activeColor = 'primary',
  activeBgColor = 'primary_20',
  size = '14px',
  padding = '6px 6px',
  gap = '0.4rem',
  chipAction,
  activeStateChange = true,
  borderColor,
  icon,
  leftIcon,
}: IChipProps) => {
  const { colors } = useTheme();
  const [active, setActive] = useState(false);

  const handleClick = () => {
    activeStateChange && setActive(!active);

    if (chipAction) {
      chipAction();
    }
  };

  return (
    <ChipWrapper
      active={active}
      bgColor={active ? activeBgColor : bgColor}
      onClick={handleClick}
      padding={padding}
      gap={gap}
      borderColor={borderColor}
    >
      {leftIcon}
      <Paragraph
        color={active ? colors[activeColor] : colors[color]}
        fontSize={size}
        fontWeight="500"
        margin="0"
        lineHeight="normal"
      >
        {text}
      </Paragraph>
      {icon}
    </ChipWrapper>
  );
};
